import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Application, CreateApplicationData } from '@/types/application'
import { applicationApi } from '@/services/api'
import { showSnackbar } from './uiSlice'
import { fetchAccessLimits } from './subscriptionSlice'

interface ApplicationsState {
    applications: Application[]
    sentApplications: Application[]
    receivedApplications: Application[]
    currentApplication: Application | null
    totalCount: number
    currentPage: number
    totalPages: number
    isLoading: boolean
    error: string | null
    stats: {
        pending: number
        accepted: number
        rejected: number
        completed: number
    }
}

const initialState: ApplicationsState = {
    applications: [],
    sentApplications: [],
    receivedApplications: [],
    currentApplication: null,
    totalCount: 0,
    currentPage: 1,
    totalPages: 1,
    isLoading: false,
    error: null,
    stats: {
        pending: 0,
        accepted: 0,
        rejected: 0,
        completed: 0,
    },
}

// Async thunks
export const fetchApplications = createAsyncThunk(
    'applications/fetchApplications',
    async (params: { page?: number; limit?: number; status?: string }, { rejectWithValue }) => {
        try {
            const response = await applicationApi.getApplications(params)
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch applications'
            return rejectWithValue(message)
        }
    }
)

export const fetchSentApplications = createAsyncThunk(
    'applications/fetchSentApplications',
    async (params: { page?: number; limit?: number; status?: string }, { rejectWithValue }) => {
        try {
            const response = await applicationApi.getSentApplications(params)
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch sent applications'
            return rejectWithValue(message)
        }
    }
)

export const fetchReceivedApplications = createAsyncThunk(
    'applications/fetchReceivedApplications',
    async (params: { page?: number; limit?: number; status?: string }, { rejectWithValue }) => {
        try {
            const response = await applicationApi.getReceivedApplications(params)
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch received applications'
            return rejectWithValue(message)
        }
    }
)

export const fetchApplicationById = createAsyncThunk(
    'applications/fetchApplicationById',
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await applicationApi.getApplicationById(id)
            return response.data.application
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch application'
            return rejectWithValue(message)
        }
    }
)

export const createApplication = createAsyncThunk(
    'applications/createApplication',
    async (data: CreateApplicationData, { rejectWithValue, dispatch }) => {
        try {
            const response = await applicationApi.createApplication(data)
            dispatch(showSnackbar({ message: 'Application submitted successfully!', type: 'success' }))
            dispatch(fetchAccessLimits())
            return response.data.application
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to submit application'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const updateApplicationStatus = createAsyncThunk(
    'applications/updateStatus',
    async ({ id, status, message }: { id: string; status: string; message?: string }, { rejectWithValue, dispatch }) => {
        try {
            const response = await applicationApi.updateApplicationStatus(id, status as 'accepted' | 'rejected' | 'completed', message)
            const statusMessages = {
                accepted: 'Application accepted!',
                rejected: 'Application rejected.',
                completed: 'Application marked as completed!',
            }
            dispatch(showSnackbar({ message: statusMessages[status as keyof typeof statusMessages] || 'Status updated!', type: 'success' }))
            return response.data.application
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to update status'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const withdrawApplication = createAsyncThunk(
    'applications/withdraw',
    async (id: string, { rejectWithValue, dispatch }) => {
        try {
            await applicationApi.withdrawApplication(id)
            dispatch(showSnackbar({ message: 'Application withdrawn successfully', type: 'success' }))
            return id
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to withdraw application'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const fetchApplicationStats = createAsyncThunk(
    'applications/fetchStats',
    async (_, { rejectWithValue }) => {
        try {
            const response = await applicationApi.getApplicationStats()
            return response.data.stats
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch stats'
            return rejectWithValue(message)
        }
    }
)

export const fetchApplicationsCount = createAsyncThunk(
    'applications/fetchApplicationsCount',
    async (userRole: 'worker' | 'employer', { rejectWithValue }) => {
        try {
            console.log('🔍 Fetching applications count for role:', userRole)
            if (userRole === 'worker') {
                // For workers, get count of applications they sent
                const response = await applicationApi.getSentApplications({ page: 1, limit: 1 })
                console.log('📊 Worker applications response:', response.data)
                console.log('📊 Worker Response data totalCount:', response.data.totalCount)
                console.log('📊 Worker Response data applications length:', response.data.applications?.length)

                // Extract count from response - totalCount is the source of truth
                const count = typeof response.data.totalCount === 'number' 
                    ? response.data.totalCount 
                    : (response.data.applications?.length ?? 0)
                
                console.log('📊 Worker Using count:', count)
                return { totalCount: count }
            } else {
                // For employers, get count of applications received for their gigs
                const response = await applicationApi.getReceivedApplications({ page: 1, limit: 1 })
                console.log('📊 Employer applications response:', response.data)
                console.log('📊 Response data totalCount:', response.data.totalCount)
                console.log('📊 Response data keys:', Object.keys(response.data))
                console.log('📊 Response data applications length:', response.data.applications?.length)

                // Extract count from response - totalCount is the source of truth
                const count = typeof response.data.totalCount === 'number' 
                    ? response.data.totalCount 
                    : (response.data.applications?.length ?? 0)
                
                console.log('📊 Using count:', count)
                return { totalCount: count }
            }
        } catch (error: any) {
            console.error('❌ Applications count fetch error:', error)
            const message = error.response?.data?.error?.message || 'Failed to fetch applications count'
            return rejectWithValue(message)
        }
    }
)

const applicationsSlice = createSlice({
    name: 'applications',
    initialState,
    reducers: {
        clearCurrentApplication: (state) => {
            state.currentApplication = null
        },
        clearError: (state) => {
            state.error = null
        },
        setCurrentPage: (state, action: PayloadAction<number>) => {
            state.currentPage = action.payload
        },
        updateApplicationInList: (state, action: PayloadAction<Application>) => {
            const updatedApp = action.payload

            // Helper function to update application in array
            const updateInArray = (array: Application[]) => {
                const index = array.findIndex(app => app._id === updatedApp._id)
                if (index !== -1) {
                    array[index] = updatedApp
                }
            }

            updateInArray(state.applications)
            updateInArray(state.sentApplications)
            updateInArray(state.receivedApplications)

            if (state.currentApplication?._id === updatedApp._id) {
                state.currentApplication = updatedApp
            }
        },
        removeApplicationFromList: (state, action: PayloadAction<string>) => {
            const appId = action.payload

            state.applications = state.applications.filter(app => app._id !== appId)
            state.sentApplications = state.sentApplications.filter(app => app._id !== appId)
            state.receivedApplications = state.receivedApplications.filter(app => app._id !== appId)

            if (state.currentApplication?._id === appId) {
                state.currentApplication = null
            }
        },
        addNewApplication: (state, action: PayloadAction<Application>) => {
            state.applications.unshift(action.payload)
            state.sentApplications.unshift(action.payload)
            state.totalCount += 1
        },
        updateStats: (state, action: PayloadAction<Partial<ApplicationsState['stats']>>) => {
            state.stats = { ...state.stats, ...action.payload }
        },
    },
    extraReducers: (builder) => {
        // Fetch Applications
        builder
            .addCase(fetchApplications.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchApplications.fulfilled, (state, action) => {
                state.isLoading = false
                state.applications = action.payload.applications
                state.totalCount = action.payload.totalCount
                state.currentPage = action.payload.currentPage
                state.totalPages = action.payload.totalPages
            })
            .addCase(fetchApplications.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Fetch Sent Applications
        builder
            .addCase(fetchSentApplications.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchSentApplications.fulfilled, (state, action) => {
                state.isLoading = false
                state.sentApplications = action.payload.applications
                // Update totalCount if we're just fetching for count purposes (limit=1)
                if (action.payload.totalCount !== undefined) {
                    state.totalCount = action.payload.totalCount
                    state.currentPage = action.payload.currentPage || 1
                    state.totalPages = action.payload.totalPages || 1
                }
            })
            .addCase(fetchSentApplications.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Fetch Received Applications
        builder
            .addCase(fetchReceivedApplications.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchReceivedApplications.fulfilled, (state, action) => {
                state.isLoading = false
                state.receivedApplications = action.payload.applications
            })
            .addCase(fetchReceivedApplications.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Fetch Application by ID
        builder
            .addCase(fetchApplicationById.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchApplicationById.fulfilled, (state, action) => {
                state.isLoading = false
                state.currentApplication = action.payload
            })
            .addCase(fetchApplicationById.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Create Application
        builder
            .addCase(createApplication.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(createApplication.fulfilled, (state, action) => {
                state.isLoading = false
                const newApplication = action.payload
                state.applications.unshift(newApplication)
                state.sentApplications.unshift(newApplication)
                state.totalCount += 1
                state.stats.pending += 1
            })
            .addCase(createApplication.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Update Application Status
        builder
            .addCase(updateApplicationStatus.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(updateApplicationStatus.fulfilled, (state, action) => {
                state.isLoading = false
                const updatedApp = action.payload

                // Update in all arrays
                const updateInArray = (array: Application[]) => {
                    const index = array.findIndex(app => app._id === updatedApp._id)
                    if (index !== -1) {
                        const oldStatus = array[index].status
                        array[index] = updatedApp

                        // Update stats
                        if (oldStatus !== updatedApp.status) {
                            state.stats[oldStatus as keyof typeof state.stats] -= 1
                            state.stats[updatedApp.status as keyof typeof state.stats] += 1
                        }
                    }
                }

                updateInArray(state.applications)
                updateInArray(state.sentApplications)
                updateInArray(state.receivedApplications)

                if (state.currentApplication?._id === updatedApp._id) {
                    state.currentApplication = updatedApp
                }
            })
            .addCase(updateApplicationStatus.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Withdraw Application
        builder
            .addCase(withdrawApplication.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(withdrawApplication.fulfilled, (state, action) => {
                state.isLoading = false
                const appId = action.payload

                // Remove from all arrays
                const removeFromArray = (array: Application[]) => {
                    const index = array.findIndex(app => app._id === appId)
                    if (index !== -1) {
                        const app = array[index]
                        state.stats[app.status as keyof typeof state.stats] -= 1
                        array.splice(index, 1)
                    }
                }

                removeFromArray(state.applications)
                removeFromArray(state.sentApplications)

                if (state.currentApplication?._id === appId) {
                    state.currentApplication = null
                }

                state.totalCount -= 1
            })
            .addCase(withdrawApplication.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Fetch Application Stats
        builder
            .addCase(fetchApplicationStats.fulfilled, (state, action) => {
                state.stats = action.payload
            })

        // Fetch Applications Count
        builder
            .addCase(fetchApplicationsCount.fulfilled, (state, action) => {
                console.log('✅ Applications count fulfilled with payload:', action.payload)
                state.totalCount = action.payload.totalCount
                console.log('✅ Applications count updated in state:', state.totalCount)
            })
            .addCase(fetchApplicationsCount.rejected, (state, action) => {
                console.error('❌ Failed to fetch applications count:', action.error)
                state.error = action.payload as string
            })
    },
})

export const {
    clearCurrentApplication,
    clearError,
    setCurrentPage,
    updateApplicationInList,
    removeApplicationFromList,
    addNewApplication,
    updateStats,
} = applicationsSlice.actions

export default applicationsSlice.reducer